import { useRef, useCallback } from 'react';
import { useSnackbar } from 'notistack';

import { Nullable } from '~globals/types/commons';
import { useLazyRequest } from '~hooks/useLazyRequest';
import { exportRoadmapDetails } from '~services/route';
import { RouteRoadmapListParams } from '~services/route/types';
import { downloadFileByRequest } from '~utils/download';
import { formatterDate } from '~utils/formatter';

interface DownloadRouteRoadmapDetailsOptions {
  onSuccess?: () => void;
  onError?: () => void;
}

interface DownloadRouteRoadmapDetailsReturn {
  loading: boolean;
  onDownloadRoadmap: (params: RouteRoadmapListParams) => Promise<void>;
}

export const useDownloadRouteRoadmapDetails = (
  options?: DownloadRouteRoadmapDetailsOptions,
): DownloadRouteRoadmapDetailsReturn => {
  const { enqueueSnackbar } = useSnackbar();
  const currentParams = useRef<Nullable<RouteRoadmapListParams>>(null);

  const [, loadingExportRoadmapDetails, , executeExportRoadmapDetails] =
    useLazyRequest({
      request: exportRoadmapDetails,
      withPostSuccess: (response) => {
        if (response.headers && response.data) {
          const prefixName = currentParams.current?.excelMode
            ? 'articulos-de-ruta'
            : 'remitos';
          const currentDate = formatterDate(new Date(), { format: 'YYYYMMDD' });
          const extension = currentParams.current?.excelMode ? 'xlsx' : 'pdf';

          const defaultFileName = `${prefixName}-${currentDate}.${extension}`;

          downloadFileByRequest(
            response.headers,
            response.data,
            defaultFileName,
          );

          enqueueSnackbar(
            `Se ha exportado el/los documentos de ruta correctamente.`,
            { variant: 'success' },
          );

          options?.onSuccess?.();
        } else {
          enqueueSnackbar(
            'No se pudo exportar el/los documentos de ruta, intente nuevamente.',
            { variant: 'error' },
          );

          options?.onError?.();
        }
      },
      withPostFailure: () => {
        enqueueSnackbar(
          'No se pudo exportar el/los documentos de ruta, intente nuevamente.',
          { variant: 'error' },
        );

        options?.onError?.();
      },
    });

  const onDownloadRoadmap = useCallback(
    async (params: RouteRoadmapListParams) => {
      currentParams.current = params;
      await executeExportRoadmapDetails(params);
      currentParams.current = null;
    },
    [executeExportRoadmapDetails],
  );

  return { loading: loadingExportRoadmapDetails, onDownloadRoadmap };
};
